<template>
  <div>
    <v-container fluid class="pt-0 px-6 mt-n16">
      <v-row>
        <v-col>
          <v-card class="card-shadow">
            <div class="card-header-padding">
              <div class="d-flex justify-space-between align-center">
                <span class="font-weight-600 text-h3 text-typo mb-0">Demo Bookings</span>
                <v-btn 
                  color="primary" 
                  @click="sendReminders" 
                  :loading="sendingReminders"
                  class="ml-2"
                >
                  <i class="material-icons-round mr-2">notifications</i>
                  Send Reminders
                </v-btn>
              </div>
            </div>
            
            <v-card-text>
              <!-- Filters -->
              <v-row class="mb-4">
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="search"
                    label="Search bookings..."
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="search"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="statusFilter"
                    :items="statusOptions"
                    label="Status"
                    outlined
                    dense
                    hide-details
                  ></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="dateFilter"
                        label="Demo Date"
                        prepend-inner-icon="event"
                        readonly
                        outlined
                        dense
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dateFilter"
                      @input="dateMenu = false"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="2">
                  <v-btn @click="clearFilters" outlined>
                    <i class="material-icons-round mr-2">clear</i>
                    Clear
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Data Table -->
              <v-data-table
                :headers="headers"
                :items="filteredBookings"
                :loading="loading"
                :search="search"
                class="elevation-1"
                :items-per-page="15"
              >
                <template v-slot:item.demo_date="{ item }">
                  {{ formatDate(item.demo_date) }}
                </template>
                
                <template v-slot:item.demo_time="{ item }">
                  {{ formatTime(item.demo_time) }}
                </template>
                
                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    small
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
                
                <template v-slot:item.confirmation_sent="{ item }">
                  <v-icon :color="item.confirmation_sent ? 'green' : 'grey'">
                    {{ item.confirmation_sent ? 'check_circle' : 'cancel' }}
                  </v-icon>
                </template>
                
                <template v-slot:item.reminder_sent="{ item }">
                  <v-icon :color="item.reminder_sent ? 'green' : 'grey'">
                    {{ item.reminder_sent ? 'check_circle' : 'cancel' }}
                  </v-icon>
                </template>
                
                <template v-slot:item.actions="{ item }">
                  <v-menu bottom left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        <i class="material-icons-round">more_vert</i>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="updateStatus(item, 'confirmed')">
                        <v-list-item-title>Mark Confirmed</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="updateStatus(item, 'completed')">
                        <v-list-item-title>Mark Completed</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="updateStatus(item, 'cancelled')">
                        <v-list-item-title>Cancel</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="updateStatus(item, 'no_show')">
                        <v-list-item-title>Mark No Show</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DemoBookings',
  data() {
    return {
      loading: false,
      sendingReminders: false,
      search: '',
      statusFilter: 'all',
      dateFilter: null,
      dateMenu: false,
      bookings: [],
      
      statusOptions: [
        { text: 'All Statuses', value: 'all' },
        { text: 'Scheduled', value: 'scheduled' },
        { text: 'Confirmed', value: 'confirmed' },
        { text: 'Completed', value: 'completed' },
        { text: 'Cancelled', value: 'cancelled' },
        { text: 'No Show', value: 'no_show' }
      ],
      
      headers: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Email', value: 'email', sortable: true },
        { text: 'Company', value: 'company', sortable: true },
        { text: 'Demo Date', value: 'demo_date', sortable: true },
        { text: 'Demo Time', value: 'demo_time', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Confirmed', value: 'confirmation_sent', sortable: false },
        { text: 'Reminder', value: 'reminder_sent', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },
  
  computed: {
    filteredBookings() {
      let filtered = this.bookings
      
      if (this.statusFilter !== 'all') {
        filtered = filtered.filter(booking => booking.status === this.statusFilter)
      }
      
      if (this.dateFilter) {
        filtered = filtered.filter(booking => booking.demo_date === this.dateFilter)
      }
      
      return filtered
    }
  },
  
  mounted() {
    this.fetchBookings()
  },
  
  methods: {
    async fetchBookings() {
      this.loading = true
      try {
        const response = await axios.get('/api/demo/bookings', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        
        if (response.data.flag === 1) {
          this.bookings = response.data.bookings.data || response.data.bookings
        }
      } catch (error) {
        console.error('Error fetching bookings:', error)
        this.$toast.error('Failed to load demo bookings')
      } finally {
        this.loading = false
      }
    },
    
    async updateStatus(booking, status) {
      try {
        const response = await axios.put(`/api/demo/bookings/${booking.id}/status`, 
          { status }, 
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        )
        
        if (response.data.flag === 1) {
          booking.status = status
          this.$toast.success('Booking status updated successfully')
        }
      } catch (error) {
        console.error('Error updating status:', error)
        this.$toast.error('Failed to update booking status')
      }
    },
    
    async sendReminders() {
      this.sendingReminders = true
      try {
        const response = await axios.post('/api/demo/send-reminders', {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        
        if (response.data.flag === 1) {
          this.$toast.success(response.data.message)
          this.fetchBookings() // Refresh to show updated reminder status
        }
      } catch (error) {
        console.error('Error sending reminders:', error)
        this.$toast.error('Failed to send reminders')
      } finally {
        this.sendingReminders = false
      }
    },
    
    clearFilters() {
      this.search = ''
      this.statusFilter = 'all'
      this.dateFilter = null
    },
    
    formatDate(date) {
      return new Date(date).toLocaleDateString()
    },
    
    formatTime(time) {
      return new Date(`2000-01-01 ${time}`).toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },
    
    getStatusColor(status) {
      const colors = {
        scheduled: 'blue',
        confirmed: 'green',
        completed: 'success',
        cancelled: 'red',
        no_show: 'orange'
      }
      return colors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
.card-header-padding {
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}
</style>
